import React from 'react';
import { EyeIcon, KeyIcon, LockIcon, SlidersHorizontalIcon } from 'lucide-react';
import { SectionIntro } from './SectionIntro';

/**
 * The IT-approval beat: the last objection standing between a team that wants
 * Giga and a company that can actually deploy it.
 *
 * It sits AFTER the product has made its case and BEFORE the ask, because this
 * is not a reason to want Giga — it is the answer to the person who has to sign
 * off on it. On dark ground, so the page visibly changes audience here.
 *
 * Four claims, not five: model independence is already argued at full width in
 * the mosaic above ("Model agnostic"), and repeating it here would spend the
 * section's strongest slot on something the reader has just read.
 *
 * No eyebrow: "Everything IT needs to say yes" already names its own audience,
 * so a "Security & enterprise" label above it was the section introducing
 * itself twice.
 */

/**
 * One drawn mark per claim, at display size. These are the only icons on the
 * page used as ILLUSTRATION rather than as labels beside text, which is why
 * they are large, hairline and unfilled: at 18px in a tinted tile they would
 * be UI chrome, and this section is making four separate promises that each
 * want a face. Chosen so no two read alike — a key, a console, an eye, a lock.
 */
const CLAIMS = [{
  title: 'Access control',
  copy: 'Granular permissions over who can build, run, and connect what.',
  Icon: KeyIcon
}, {
  title: 'One console',
  copy: 'Set how AI behaves company-wide from a single place, not per person.',
  Icon: SlidersHorizontalIcon
}, {
  title: 'Full visibility',
  copy: 'Usage and spend analytics across every workflow and every team.',
  Icon: EyeIcon
}, {
  title: 'Secure infrastructure',
  copy: 'Isolated cloud environments, with your credentials held in Giga and never handed to a model provider.',
  Icon: LockIcon
}] as const;
export function SecurityEnterprise() {
  return <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-1061" data-mp="2n2lk8p6p0owp">
      <SectionIntro align="left" className="max-w-[760px]" data-id="element-1062" data-mp="2oku0b4xhi490">
        <h2 className="font-title text-[32px] font-bold leading-[1.08] tracking-tight text-[#FAF8F4] sm:text-4xl md:text-[56px] md:leading-[1.14]" data-id="element-1063" data-mp="2oj91h344zz5c">
          Everything IT needs to say yes<span className="text-[#EA580C]" data-id="element-1064" data-mp="2fk5hlwx5bfps">.</span>
        </h2>
        <p className="mt-4 max-w-[56ch] text-[16.5px] leading-relaxed text-[#C9BFB4] md:mt-5 md:text-lg" data-id="element-1065" data-mp="2kiyz00fjzqv8">
          Giga is built for security- and privacy-conscious companies: give your
          whole team AI without giving up control. Your keys stay protected;
          your data stays yours.
        </p>
      </SectionIntro>

      {/* Two by two. With four claims of unequal length, a four-across row
       forced the shortest card to carry the height of the longest; paired, the
       measures sit close enough that the rows read as level. */}
      <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 md:grid-cols-2 md:gap-5" data-id="element-1066" data-mp="1my2y6qcp369z">
        {CLAIMS.map(claim => <article key={claim.title} className="flex flex-col rounded-[18px] border border-white/[0.1] bg-white/[0.03] p-6 md:p-7" data-id="element-1067" data-mp="1rwxftoyxeosi">
            {/* Sized as an image and given real clearance above the heading,
             so it reads as the card's subject rather than as a bullet. 1.25
             stroke keeps it hairline at 44px — lucide's default weight looks
             clumsy this large. */}
            <claim.Icon aria-hidden="true" size={44} strokeWidth={1.25} className="mb-7 text-[#FAF8F4] md:mb-9" data-id="element-1068" data-mp="1daqkr71hpw54" />
            <h3 className="font-title text-[20px] font-bold leading-[1.2] tracking-[-0.02em] text-[#FAF8F4]" data-id="element-1069" data-mp="1m9c1cwnej139">
              {claim.title}
            </h3>
            <p className="mt-2.5 max-w-[44ch] text-[14.5px] leading-relaxed text-[#C9BFB4]" data-id="element-1070" data-mp="20xfaug0nkuwt">
              {claim.copy}
            </p>
          </article>)}
      </div>

      {/* Stated as in progress rather than badged. An amber dot and the plain
       word "in progress" is the honest form of this claim — a certification
       seal implies an audit that has already closed, and the reader who cares
       about SOC 2 is exactly the reader who would check. */}
      <p className="mt-8 flex items-center gap-2.5 text-[14px] text-[#C9BFB4] md:mt-10" data-id="element-1071" data-mp="88peglo0ybv3">
        <span aria-hidden="true" className="size-[7px] shrink-0 rounded-full bg-[#FB923C]" data-id="element-1072" data-mp="cznx3rwhjm1i" />
        SOC 2 Type II compliance in progress
      </p>
    </div>;
}